import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';
import { TOTAL_QUESTIONS } from '../../data/questions';

interface ProgressBarProps {
  currentStep: number;
  totalSteps?: number;
}

export function ProgressBar({
  currentStep,
  totalSteps = TOTAL_QUESTIONS,
}: ProgressBarProps) {
  const progress = (currentStep / totalSteps) * 100;

  return (
    <div className="w-full space-y-3">
      <div className="flex justify-between items-center text-sm">
        <span className="text-slate-400">
          Question{' '}
          <span className="text-primary-400 font-semibold">{currentStep}</span>{' '}
          of {totalSteps}
        </span>
        <span className="text-slate-500">{Math.round(progress)}% complete</span>
      </div>

      <div className="relative h-2 bg-slate-800 rounded-full overflow-hidden">
        <motion.div
          className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary-500 to-accent-500 rounded-full"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ type: 'spring', stiffness: 100, damping: 20 }}
        />
      </div>

      <div className="flex justify-between">
        {Array.from({ length: totalSteps }, (_, i) => {
          const stepNum = i + 1;
          const isCompleted = stepNum < currentStep;
          const isCurrent = stepNum === currentStep;

          return (
            <motion.div
              key={stepNum}
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-colors',
                isCompleted &&
                  'bg-primary-500/20 text-primary-400 border border-primary-500/30',
                isCurrent &&
                  'bg-primary-500 text-white shadow-lg shadow-primary-500/30',
                !isCompleted &&
                  !isCurrent &&
                  'bg-slate-800 text-slate-500 border border-slate-700'
              )}
              initial={false}
              animate={{
                scale: isCurrent ? 1.1 : 1,
              }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            >
              {isCompleted ? (
                <Check className="w-4 h-4" />
              ) : (
                stepNum
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
